import { useEffect, useState, useCallback, useRef, Fragment } from 'react';
import { MobileHeader, HeaderAction, ActionSheet, useCashboxCourse } from '../components/MobileHeader';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO, addDays, addMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  ArrowLeft, Plus, Trash2, Edit2, ChevronDown, ChevronUp, ChevronRight,
  CheckCircle, XCircle, Users, MapPin, Calendar, BookOpen, ExternalLink, Repeat, Ban, CheckCheck, Banknote, MoreVertical, MoreHorizontal, CreditCard, UserPlus, X, Power, Columns3, Calculator
} from 'lucide-react';
import { toast } from 'sonner';
import StudentModal from '../components/StudentModal';
import { inRoleGroup, enrollBadge, ENROLL_CHOICES } from '../lib/enrollment';
import { sessionAmount, paidBySession, sessionTotal as calcSessionTotal } from '../lib/sessionAmounts';
import { useLang } from '../i18n';
import { useAuth } from '../AuthContext';
import { api } from '../api';
import { Avatar } from './Dashboard';
import ClassModal from '../components/ClassModal';
import { GroupCourseModal } from './GroupClasses';
import { cn } from '../lib/utils';

/* ── Menu actions mobile (Terminer / Supprimer) ── */
function MobileActionMenu({ onComplete, onDelete, isCompleted }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(v => !v)}
        className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-slate-200 hover:bg-slate-50 transition-colors">
        <MoreVertical size={17} className="text-slate-600" />
      </button>
      {open && (
        <div className="absolute right-0 top-12 bg-white rounded-xl shadow-xl border border-slate-100 z-50 min-w-[160px] py-1 overflow-hidden">
          {!isCompleted && onComplete && (
            <button onClick={() => { onComplete(); setOpen(false); }}
              className="flex items-center gap-2 w-full px-4 py-3 text-sm font-semibold text-[#26A69A] hover:bg-[#26A69A]/5 transition-colors">
              <CheckCheck size={14} /> Terminer le cours
            </button>
          )}
          <button onClick={() => { onDelete(); setOpen(false); }}
            className="flex items-center gap-2 w-full px-4 py-3 text-sm font-semibold text-red-500 hover:bg-red-50 transition-colors">
            <Trash2 size={14} /> Supprimer
          </button>
        </div>
      )}
    </div>
  );
}

const DAYS_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const DAYS_EN = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const fmtDuration = (m) => `${Math.floor(m / 60)}h${m % 60 ? m % 60 : ''}`;

function getNextOccurrence(dayOfWeek, startTime) {
  const now = new Date();
  const jsDow = (dayOfWeek + 1) % 7;
  const today = now.getDay();
  let daysUntil = (jsDow - today + 7) % 7;
  if (daysUntil === 0) daysUntil = 7;
  const next = addDays(now, daysUntil);
  const [h, m] = startTime.split(':');
  next.setHours(parseInt(h), parseInt(m), 0, 0);
  return format(next, "yyyy-MM-dd'T'HH:mm");
}

export default function GroupClassDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const locale = lang === 'fr' ? fr : undefined;
  const DAYS = lang === 'fr' ? DAYS_FR : DAYS_EN;

  const [course, setCourse] = useState(null);
  const [allStudents, setAllStudents] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tab, setTab] = useState('sessions');
  // Leaders ouverts par défaut, Inactifs + Sans rôle fermés
  const [collapsedGroups, setCollapsedGroups] = useState(new Set(['inactive', 'unassigned']));
  const [infoExpanded, setInfoExpanded] = useState(false);
  const [expandedSession, setExpandedSession] = useState(null);
  const [showAddSession, setShowAddSession] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showEditCourse, setShowEditCourse] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [coursePayments, setCoursePayments] = useState([]);
  const [packagesByStudent, setPackagesByStudent] = useState({});
  const [pricingMatrix, setPricingMatrix] = useState([]);
  const [sessionMenu, setSessionMenu] = useState(null);
  const [courseMenu, setCourseMenu] = useState(false);  // mobile : menu des actions du cours
  useCashboxCourse(id);  // la caisse 💵+ propose d'abord les élèves de ce cours  // mobile : séance dont le menu d'actions est ouvert
  const tableScrollRef = useRef(null);
  const hasAutoScrolled = useRef(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [amountsView, setAmountsView] = useState(false);  // v2.23 : vue Montants, lecture seule
  const [syntheticView, setSyntheticView] = useState(() => {
    try { return JSON.parse(localStorage.getItem('gc_synth') || 'false'); } catch { return false; }
  });
  const toggleSynth = () => setSyntheticView(v => {
    const nv = !v; localStorage.setItem('gc_synth', JSON.stringify(nv)); return nv;
  });

  const handleToggleActive = async () => {
    try {
      await api.patch(`/group-courses/${id}/active`, { is_active: !(course?.is_active !== false) });
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- id is the only meaningful trigger; all others are stable
  const fetchData = useCallback(async () => {
    const [c, s, l, pays, matrix] = await Promise.all([
      api.get(`/group-courses/${id}`),
      api.get('/students'),
      api.get('/locations'),
      api.get(`/payments?group_course_id=${id}`),
      api.get('/pricing-matrix').catch(() => []),
    ]);
    setCourse(c);
    setAllStudents(s);
    setLocations(l);
    setCoursePayments(pays || []);
    setPricingMatrix(matrix || []);
    // Fetch packages for enrolled students to show "Forfait" badge
    const enrolledIds = (c.enrolled_students || []).map(e => e.student_id);
    if (enrolledIds.length > 0) {
      const pkgBatch = await api.get(`/packages/batch?student_ids=${enrolledIds.join(',')}`).catch(() => ({}));
      setPackagesByStudent(pkgBatch || {});
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-scroll: position the table to show the last 2 past sessions on load
  useEffect(() => {
    if (hasAutoScrolled.current || !tableScrollRef.current || !course) return;
    const allSorted = [...(course.sessions || [])].sort((a, b) => a.datetime_start.localeCompare(b.datetime_start));
    const pastCount = allSorted.filter(s => s.is_past).length;
    if (pastCount < 2) return;
    tableScrollRef.current.scrollLeft = Math.max(0, pastCount - 2) * 60;
    hasAutoScrolled.current = true;
  }, [course]);

  const handleDeleteCourse = async () => {
    const sessionCount = sessions.length;
    const msg = sessionCount > 0
      ? `Supprimer ce cours et ses ${sessionCount} séances associées ? Il va dans la Corbeille (Profil › Corbeille), restaurable.`
      : 'Supprimer ce cours ? Il va dans la Corbeille (Profil › Corbeille), restaurable.';
    if (!window.confirm(msg)) return;
    const res = await api.delete(`/group-courses/${id}`);
    toast.success(`Cours supprimé${res.slots_deleted ? ` (${res.slots_deleted} séances supprimées)` : ''}`);
    navigate('/group-classes');
  };

  const handleCompleteCourse = async () => {
    const future = sessions.filter(s => !s.is_past && s.status !== 'cancelled').length;
    const msg = future > 0
      ? `Terminer ce cours ? Les ${future} séances futures non terminées seront supprimées du planning. Les séances passées et terminées restent intactes.`
      : 'Marquer ce cours comme terminé ?';
    if (!window.confirm(msg)) return;
    const res = await api.post(`/group-courses/${id}/complete`, {});
    toast.success(`Cours terminé${res.future_sessions_removed ? ` · ${res.future_sessions_removed} séances futures supprimées` : ''}`);
    fetchData();
  };

  const handleUnenroll = async (studentId) => {
    await api.delete(`/group-courses/${id}/students/${studentId}`);
    toast.success('Élève désinscrit');
    fetchData();
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400">{t('common.loading')}</div>;
  if (!course) return <div className="text-center py-20 text-slate-400">Cours introuvable</div>;

  const sessions = course.sessions || [];
  const pastSessions = sessions.filter(s => s.is_past);
  const futureSessions = sessions.filter(s => !s.is_past);
  const enrolledStudents = course.enrolled_students || [];
  const unenrolledStudents = allStudents.filter(s => !enrolledStudents.find(e => e.student_id === s.id));

  const filteredEnrolled = statusFilter === 'all'
    ? enrolledStudents
    : enrolledStudents.filter(e => (e.status || 'active') === statusFilter);

  const prefilledSession = {
    type: 'group',
    title: course.name,
    datetime_start: getNextOccurrence(course.day_of_week, course.start_time),
    duration_minutes: course.duration_minutes,
    location_id: course.location_id,
    group_course_id: id,
  };

  return (
    <div className="max-w-5xl mx-auto w-full" style={{ overflowX: 'clip' }} data-testid="group-class-detail">
      {/* Mobile : en-tête commun (← · nom du cours · actions) */}
      <MobileHeader title={course.name} back="/group-classes" actions={user?.role === 'admin' ? <>
        <HeaderAction icon={UserPlus} label="Ajouter un élève" primary onClick={() => setShowEnrollModal(true)} testId="add-student-header-btn-mobile" />
        <HeaderAction icon={MoreHorizontal} label="Actions du cours" onClick={() => setCourseMenu(true)} testId="course-menu-btn" />
      </> : (
        // Assistant / viewer : pas de menu ⋯, la vue synthèse reste dans la barre ; v2.29 : l'assistante peut ajouter un élève
        <>
          {user?.role === 'assistant' && <HeaderAction icon={UserPlus} label="Ajouter un élève" primary onClick={() => setShowEnrollModal(true)} testId="add-student-header-btn-mobile" />}
          <HeaderAction icon={Columns3} label="Vue synthèse" active={syntheticView} onClick={toggleSynth} testId="toggle-synthetic-btn-mobile" />
        </>
      )} />
      <ActionSheet open={courseMenu} onClose={() => setCourseMenu(false)} title={course.name} testId="course-menu-sheet" actions={[
        { icon: Columns3, label: syntheticView ? 'Afficher toutes les séances' : 'Vue synthèse (3 dernières + 2 prochaines)', onClick: toggleSynth },
        { icon: Calculator, label: amountsView ? 'Revenir au tableau des présences' : 'Montants par élève (lecture seule)', onClick: () => setAmountsView(v => !v) },
        { icon: CreditCard, label: 'Caisse fin de cours (détail par séance)', className: 'text-[#26A69A]', onClick: () => setShowCheckout(true) },
        { icon: Repeat, label: 'Générer les séances', className: 'text-[#0288D1]', onClick: () => setShowGenerateModal(true) },
        { icon: Plus, label: 'Ajouter une séance ponctuelle', onClick: () => setShowAddSession(true) },
        { icon: Edit2, label: 'Modifier le cours', onClick: () => setShowEditCourse(true) },
        { icon: Power, label: course.is_active !== false ? 'Passer en inactif' : 'Passer en actif', onClick: handleToggleActive },
        ...(course.status !== 'completed' ? [{ icon: CheckCheck, label: 'Terminer le cours', className: 'text-[#26A69A]', onClick: handleCompleteCourse }] : []),
        { icon: Trash2, label: 'Supprimer le cours', className: 'text-red-500', onClick: handleDeleteCourse },
      ]} />
      {/* Header */}
      <div className="mobile-hidden flex items-center gap-2 mb-4">
        <button onClick={() => navigate('/group-classes')}
          className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-slate-200 hover:bg-slate-50 transition-colors shrink-0"
          data-testid="back-btn">
          <ArrowLeft size={18} className="text-slate-600" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {course.color && <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: course.color }} />}
            <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900 font-manrope truncate">{course.name}</h1>
            <button
              onClick={handleToggleActive}
              className={cn('px-2.5 py-1 rounded-full text-xs font-bold transition-all border shrink-0',
                course.is_active !== false
                  ? 'bg-[#26A69A]/10 text-[#26A69A] border-[#26A69A]/20 hover:bg-[#26A69A]/20'
                  : 'bg-slate-100 text-slate-400 border-slate-200 hover:bg-slate-200')}
              data-testid="toggle-course-active-btn">
              {course.is_active !== false ? 'Actif' : 'Inactif'}
            </button>
          </div>
          {course.level && course.level.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {(Array.isArray(course.level) ? course.level : [course.level]).map(l => (
                <span key={l} className="text-xs font-semibold text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full border border-slate-200">{l}</span>
              ))}
            </div>
          )}
        </div>
        {/* Actions regroupées en mobile */}
        <button onClick={() => setShowEnrollModal(true)}
          className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#0288D1] text-white hover:bg-[#0277BE] transition-colors shrink-0"
          title="Ajouter un élève"
          data-testid="add-student-header-btn">
          <UserPlus size={16} />
        </button>
        <button onClick={() => setShowEditCourse(true)}
          className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-slate-200 hover:bg-slate-50 transition-colors shrink-0"
          data-testid="edit-course-btn">
          <Edit2 size={16} className="text-slate-600" />
        </button>
        <div className="hidden sm:flex items-center gap-2">
          {course.status !== 'completed' ? (
            <button onClick={handleCompleteCourse}
              className="flex items-center gap-1.5 px-3 h-10 rounded-xl bg-white border border-[#26A69A]/30 text-[#26A69A] text-xs font-bold hover:bg-[#26A69A]/5 transition-colors"
              data-testid="complete-course-btn">
              <CheckCheck size={13} /> Terminer
            </button>
          ) : (
            <span className="flex items-center gap-1.5 px-3 h-10 rounded-xl bg-[#26A69A]/10 text-[#26A69A] text-xs font-bold">
              <CheckCheck size={13} /> Terminé
            </span>
          )}
          <button onClick={handleDeleteCourse}
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-red-100 hover:bg-red-50 transition-colors"
            data-testid="delete-course-btn">
            <Trash2 size={16} className="text-red-500" />
          </button>
        </div>
        {/* Mobile : menu actions secondaires */}
        <div className="sm:hidden">
          <MobileActionMenu
            onComplete={course.status !== 'completed' ? handleCompleteCourse : null}
            onDelete={handleDeleteCourse}
            isCompleted={course.status === 'completed'}
          />
        </div>
      </div>

      {/* Info card — accordéon : ligne 1 toujours visible, reste au clic */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-4 overflow-hidden">
        <button
          onClick={() => setInfoExpanded(v => !v)}
          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-600 text-left hover:bg-slate-50/50 transition-colors"
          data-testid="info-card-toggle"
        >
          <div className="flex items-center gap-2 shrink-0">
            <Calendar size={14} className="text-[#0288D1]" />
            <span className="font-semibold">{DAYS[course.day_of_week]} · {course.start_time}</span>
            <span className="text-slate-400">({fmtDuration(course.duration_minutes)})</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <MapPin size={14} className="text-[#26A69A]" />
            <span className="truncate">{course.location?.venue_name} · {course.location?.city}</span>
          </div>
          <ChevronDown size={13} className={cn('ml-auto text-slate-300 shrink-0 transition-transform duration-200', infoExpanded && 'rotate-180')} />
        </button>
        {infoExpanded && (
          <div className="px-4 pb-3 flex flex-wrap gap-4 text-sm text-slate-600 border-t border-slate-50">
            <div className="flex items-center gap-2">
              <Users size={14} className="text-slate-400" />
              <span className="font-bold text-slate-800">{course.total_enrolled}</span>
              <span className="text-slate-500">actifs</span>
              <span className="text-[#0288D1] font-semibold">· {course.active_leader_count ?? course.leader_count ?? 0}L</span>
              <span className="text-pink-500 font-semibold">{course.active_follower_count ?? course.follower_count ?? 0}F</span>
              {(course.inactive_count ?? 0) > 0 && (
                <span className="text-slate-400 font-medium">· {course.inactive_count} <span className="text-slate-300">inactifs</span></span>
              )}
            </div>
            {sessions.length > 0 && (
              <div className="flex items-center gap-2">
                <Calendar size={14} className="text-slate-400" />
                <span className="font-bold text-slate-800">{sessions.length}</span> séances total
                <span className="text-[#26A69A] font-semibold">· {futureSessions.length} restantes</span>
              </div>
            )}
            {course.description && <p className="text-xs text-slate-400 w-full">{course.description}</p>}
          </div>
        )}
      </div>

      {/* ── Barre onglet Séances + actions sur une seule ligne dense (PC ; sur mobile → barre du haut et menu ⋯) ── */}
      <div className="mobile-hidden flex items-center gap-1.5 mb-4 border-b border-slate-100 pb-2">
        <span className="shrink-0 text-sm font-bold text-[#0288D1] border-b-2 border-[#0288D1] pb-1 px-1">
          {t('groupClasses.sessionsTab')}
          <span className="ml-1 text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{sessions.length}</span>
        </span>
        <div className="flex-1" />
        {/* Synthèse */}
        <button onClick={toggleSynth}
          className={cn('h-8 px-2 rounded-lg font-bold flex items-center gap-1 text-xs border transition-all',
            syntheticView ? 'bg-[#0288D1]/10 border-[#0288D1] text-[#0288D1]' : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300')}
          title="Vue synthétique: 3 derniers + 2 prochains"
          data-testid="toggle-synthetic-btn">
          <Banknote size={13} />
          <span className="hidden sm:inline text-[11px]">Synthèse</span>
        </button>
        {/* Caisse */}
        <button onClick={() => setShowCheckout(true)}
          className="h-8 px-2 bg-white border border-[#26A69A] text-[#26A69A] rounded-lg font-bold flex items-center gap-1 hover:bg-[#26A69A]/5 transition-all"
          data-testid="checkout-btn">
          <CreditCard size={13} />
          <span className="hidden sm:inline text-[11px]">Caisse</span>
        </button>
        {/* Récurrences */}
        <button onClick={() => setShowGenerateModal(true)}
          className="h-8 px-2 bg-white border border-[#0288D1] text-[#0288D1] rounded-lg font-bold flex items-center gap-1 hover:bg-[#0288D1]/5 transition-all"
          data-testid="generate-sessions-btn">
          <Repeat size={13} />
          <span className="hidden sm:inline text-[11px]">Répét.</span>
        </button>
        {/* + Séance */}
        <button onClick={() => setShowAddSession(true)}
          className="h-8 w-8 bg-[#0288D1] text-white rounded-lg font-bold flex items-center justify-center hover:bg-[#0277BE] transition-all"
          title={t('groupClasses.addSession')}
          data-testid="add-session-btn">
          <Plus size={15} />
        </button>
      </div>

      {/* ── SESSIONS TAB ── Vue tableau (dates × élèves) ── */}
      <div>

          {sessions.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-2xl border border-slate-100 text-slate-400 text-sm">
              <BookOpen size={32} className="mx-auto mb-2 opacity-30" />
              {t('groupClasses.noSessions')}
            </div>
          ) : (() => {
            const allSorted = [...sessions].sort((a, b) => a.datetime_start.localeCompare(b.datetime_start));
            // Synthetic view: last 3 past + next 2 future
            const sessionsSortedAsc = syntheticView ? (() => {
              const past = allSorted.filter(s => s.is_past);
              const future = allSorted.filter(s => !s.is_past && s.status !== 'cancelled');
              return [...past.slice(-3), ...future.slice(0, 2)];
            })() : allSorted;
            // Assistant : peut modifier les séances des 2 dernières semaines
            const twoWeeksAgo = new Date(); twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
            const last4Ids = new Set(
              allSorted.filter(s => new Date(s.datetime_start) >= twoWeeksAgo).map(s => s.id)
            );
            // Separate active and inactive
            const isActive = inRoleGroup;  // v2.24 : actifs ET essais dans leur groupe
            const followers = enrolledStudents.filter(e => e.role === 'follower' && isActive(e));
            const leaders   = enrolledStudents.filter(e => e.role === 'leader' && isActive(e));
            const unassigned = enrolledStudents.filter(e => e.role !== 'follower' && e.role !== 'leader' && isActive(e));
            const inactive   = enrolledStudents.filter(e => !isActive(e));
            // v2.23.3 : vue Montants = le MÊME tableau, chaque pastille affiche le montant compté (lecture seule)
            const paidMaps = Object.fromEntries(sessionsSortedAsc.map(ss => [ss.id, paidBySession(ss, coursePayments)]));
            const amountOf = (ss, sid, billing) => sessionAmount(ss.attendances?.find(a => a.student_id === sid), paidMaps[ss.id][sid] || 0, billing, course);
            const fmtAmt = (n) => (Math.round(n * 10) / 10).toString();

            const renderRows = (group) => group.map(({ student, student_id, billing, status: enrollStatus, role: enrollRole, billing_status: enrollBillingStatus, added_by: addedBy }) => {
              const totalNonCancelled = sessionsSortedAsc.filter(s => !s.is_cancelled && s.status !== 'cancelled').length;
              const presentCount = sessionsSortedAsc.filter(s => {
                if (s.is_cancelled || s.status === 'cancelled') return false;
                const att = s.attendances?.find(a => a.student_id === student_id);
                return att?.present || att?.is_aide || att?.is_trial;
              }).length;
              const isEnrollActive = inRoleGroup({ status: enrollStatus });
              const billingRow = pricingMatrix.find(r => r.status_name === (enrollBillingStatus ?? 'Standard'));
              const currentCode = billingRow?.status_code || '--';
              const firstName = student?.first_name || student?.name?.split(' ')[0] || 'Élève';
              const lastName = student?.last_name || (student?.name?.split(' ').slice(1).join(' ')) || '';
              return (
              <tr key={student_id} className={cn('border-b border-slate-100 hover:bg-slate-50/60 transition-colors',
                !isEnrollActive && 'opacity-60')}>
                {/* ── Colonne Identité (sticky left:0, 78px) — PREMIÈRE ── */}
                <td className="sticky left-0 z-10 bg-white border-r border-slate-100 px-1.5" style={{ height: 41, width: 78, minWidth: 78, left: 0 }}>
                  {(() => {
                    const inner = (<>
                      <div className="flex items-baseline gap-0.5 leading-tight">
                        <span className="text-[12px] font-bold text-slate-800 truncate">{firstName}</span>
                        {amountsView
                          ? <span className="text-[9px] text-[#26A69A] shrink-0 font-bold ml-0.5" data-testid="amounts-row-total">
                              {fmtAmt(sessionsSortedAsc.reduce((t, ss) => t + amountOf(ss, student_id, billing).amount, 0))}.-</span>
                          : <span className="text-[9px] text-slate-400 shrink-0 font-normal ml-0.5">{presentCount}/{totalNonCancelled}</span>}
                      </div>
                      <span className="text-[12px] font-bold text-slate-600 truncate leading-tight">{lastName}</span>
                      {addedBy && user?.role === 'admin' && (
                        <span className="text-[8px] font-semibold text-violet-500 truncate leading-none" title={`Ajouté par ${addedBy}`} data-testid="added-by">+ {addedBy.split(' ')[0]}</span>
                      )}
                    </>);
                    return amountsView
                      ? <div className="flex flex-col justify-center gap-0 text-left w-full h-full">{inner}</div>
                      : <button onClick={() => navigate(`/students/${student_id}`)} className="flex flex-col justify-center gap-0 text-left w-full h-full">{inner}</button>;
                  })()}
                </td>
                {/* ── Colonne Contrôle (sticky left:78, 30px) — DEUXIÈME ── */}
                <td className="sticky z-20 bg-white border-r-2 border-slate-200" style={{ height: 41, width: 30, minWidth: 30, maxWidth: 30, left: 78 }}>
                  {(amountsView || user?.role !== 'admin') ? (
                    <div className="flex flex-col items-center justify-center gap-1 h-full py-0.5 text-[11px] font-bold" data-testid={`ctrl-static-${student_id}`}>
                      <span className={enrollBadge(enrollStatus).cls}>[{enrollBadge(enrollStatus).code}]</span>
                      <span className={enrollBillingStatus && enrollBillingStatus !== 'Standard' ? 'text-orange-500' : 'text-slate-400'}>[{currentCode}]</span>
                    </div>
                  ) : (
                  <div className="flex flex-col items-center justify-center gap-1 h-full py-0.5">
                    <InlineControlMenu
                      testId={`ctrl-status-${student_id}`}
                      trigger={
                        <span className={cn('font-bold text-[11px]', enrollBadge(enrollStatus).cls)}>
                          [{enrollBadge(enrollStatus).code}]
                        </span>
                      }
                      options={ENROLL_CHOICES.map(v => ({ value: v, label: enrollBadge(v).label, active: (enrollStatus || 'active') === v }))}
                      onSelect={async (val) => {
                        await api.put(`/group-courses/${id}/students/${student_id}`, { status: val });
                        fetchData();
                      }}
                    />
                    <InlineControlMenu
                      testId={`ctrl-billing-${student_id}`}
                      trigger={
                        <span className={cn('font-bold text-[11px]', enrollBillingStatus && enrollBillingStatus !== 'Standard' ? 'text-orange-500' : 'text-slate-400')}>
                          [{currentCode}]
                        </span>
                      }
                      options={pricingMatrix.length > 0 ? pricingMatrix.map(row => ({
                        value: row.status_name,
                        label: row.status_code ? `[${row.status_code}] ${row.status_name}` : row.status_name,
                        active: (enrollBillingStatus ?? 'Standard') === row.status_name,
                      })) : [{ value: 'Standard', label: 'Standard', active: true }]}
                      onSelect={async (val) => {
                        await api.put(`/group-courses/${id}/students/${student_id}`, { billing_status: val });
                        fetchData();
                      }}
                    />
                  </div>
                  )}
                </td>
                {sessionsSortedAsc.map(s => {
                  const att = s.attendances?.find(a => a.student_id === student_id);
                  const isCancelled = s.is_cancelled || s.status === 'cancelled';
                  // Date du cours (format YYYY-MM-DD)
                  const classDate = s.datetime_start ? s.datetime_start.slice(0, 10) : null;
                  return (
                    <td key={s.id} className="px-1 text-center border-r border-slate-50 last:border-r-0" style={{ height: 41, width: 60, minWidth: 60 }}>
                      {isCancelled ? (
                        <div className="h-full flex items-center justify-center">
                          <span className="text-slate-200 text-xs">—</span>
                        </div>
                      ) : (
                        <div className="h-full flex items-center justify-center">
                          {amountsView
                            ? <AmountPill att={att} look={amountOf(s, student_id, billing)} fmt={fmtAmt} />
                            : <AttendanceCycleBtn classId={s.id} studentId={student_id} att={att} onChanged={fetchData} classDate={classDate} userRole={user?.role} isRecentSession={last4Ids.has(s.id)} />}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
              );
            });

            // Group header row with inline subtotals per session
            const renderGroupHeader = (group, label, labelClass, bgClass, groupKey) => {
              if (group.length === 0) return null;
              const isCollapsed = collapsedGroups.has(groupKey);
              const toggleGroup = () => setCollapsedGroups(prev => {
                const n = new Set(prev);
                n.has(groupKey) ? n.delete(groupKey) : n.add(groupKey);
                return n;
              });
              // v2.14 : les cellules fixes à gauche ont un fond PLEIN (sinon les chiffres qui défilent se voient à travers)
              const solidBg = bgClass.includes('pink') ? 'bg-pink-50' : bgClass.includes('0288D1') ? 'bg-sky-50' : 'bg-white';
              return (
                <tr className={bgClass}>
                  {/* Identity cell: group label seulement — PREMIÈRE */}
                  <td className={cn('sticky left-0 z-10 px-1.5 py-1.5 border-r border-slate-100', solidBg)} style={{ minWidth: 78, left: 0 }}>
                    <button onClick={toggleGroup}
                      className="flex items-center gap-1 text-left"
                      data-testid={`group-header-${groupKey}`}>
                      <ChevronRight size={11}
                        className={cn('transition-transform duration-200 text-slate-400', !isCollapsed && 'rotate-90')} />
                      <span className={cn('text-[9px] font-extrabold uppercase tracking-wider', labelClass)}>
                        {label}
                      </span>
                    </button>
                  </td>
                  {/* Ctrl cell: count — DEUXIÈME */}
                  <td className={cn('sticky z-10 border-r-2 border-slate-200 text-center', solidBg)} style={{ width: 30, minWidth: 30, left: 78 }}>
                    <span className={cn('text-[10px] font-extrabold', labelClass)}>{group.length}</span>
                  </td>
                  {sessionsSortedAsc.map(s => {
                    const isCancelled = s.is_cancelled || s.status === 'cancelled';
                    if (isCancelled) return <td key={s.id} className="px-1 text-center"><span className="text-slate-200 text-[9px]">—</span></td>;
                    const total = group.filter(({ student_id }) => {
                      const a = s.attendances?.find(a => a.student_id === student_id);
                      return a?.present || a?.is_aide;
                    }).length;
                    if (amountsView) {
                      const sub = group.reduce((t, { student_id, billing }) => t + amountOf(s, student_id, billing).amount, 0);
                      return (
                        <td key={s.id} className="px-1 text-center border-r border-slate-50 last:border-r-0">
                          <span className={cn('text-[11px] font-bold', labelClass, sub === 0 && 'opacity-40')}>{fmtAmt(sub)}</span>
                        </td>
                      );
                    }
                    return (
                      <td key={s.id} className="px-1 text-center border-r border-slate-50 last:border-r-0">
                        <span className={cn('text-[11px] font-bold', labelClass, total === 0 && 'opacity-40')}>
                          {total}<span className="text-[9px] font-normal opacity-60">/{group.length}</span>
                        </span>
                      </td>
                    );
                  })}
                </tr>
              );
            };

            return (
              <div className="rounded-2xl border border-slate-100 shadow-sm" style={{ maxWidth: '100vw', overflow: 'hidden' }}>
                <div ref={tableScrollRef} className="bg-white" style={{ maxHeight: '65vh', overflowX: 'scroll', overflowY: 'auto', WebkitOverflowScrolling: 'touch', width: '100%' }}>
                <table className="border-collapse" style={{ minWidth: `${108 + sessionsSortedAsc.length * 60}px` }}>
                  <thead className="sticky top-0 z-20 bg-slate-50">
                    <tr className="bg-slate-50 border-b-2 border-slate-100">
                      {/* Identity header — PREMIÈRE */}
                      <th className="sticky left-0 z-30 bg-slate-50 px-1.5 py-2 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider border-r border-slate-100" style={{ width: 78, minWidth: 78, left: 0 }}>
                        <span className="flex items-center gap-1">Élève
                          {user?.role === 'admin' && <AmountsToggle active={amountsView} onClick={() => setAmountsView(v => !v)} />}</span>
                      </th>
                      {/* Ctrl header — DEUXIÈME */}
                      <th className="sticky z-30 bg-slate-50 border-r-2 border-slate-200" style={{ width: 30, minWidth: 30, maxWidth: 30, left: 78 }} />
                      {sessionsSortedAsc.map(s => {
                        const isCancelledH = s.is_cancelled || s.status === 'cancelled';
                        const isCompletedH = s.is_completed || s.status === 'completed';
                        const isFutureH = !s.is_past && !isCompletedH;
                        const dateLabel = (() => { try { return format(parseISO(s.datetime_start), 'dd/MM', { locale }); } catch { return '—'; } })();
                        const totalPresent = enrolledStudents.filter(({ student_id }) => {
                          const a = s.attendances?.find(a => a.student_id === student_id);
                          return a?.present || a?.is_aide;
                        }).length;
                        // Total per session: payments received + unpaid-but-present at agreed price
                        // Essais et aides exclus du CA (règle partagée avec la vue « Montants », v2.23)
                        const sessionTotal = calcSessionTotal(s, enrolledStudents, coursePayments, course);
                        return (
                          <th key={s.id} className={cn('px-1 py-1.5 text-center border-r border-slate-100 last:border-r-0 bg-slate-50', isCancelledH && 'opacity-50')} style={{ width: 60, minWidth: 60 }}>
                            {/* Total élèves présents au-dessus de la date */}
                            <div className={cn('font-extrabold mb-0.5', totalPresent > 0 ? 'text-slate-800' : 'text-slate-300')}>
                              {totalPresent > 0
                                ? <span className="text-[11px]">{totalPresent}<span className="text-[8px] font-semibold ml-0.5 opacity-60">pers</span></span>
                                : <span className="text-[12px]">·</span>
                              }
                            </div>
                            <div className={cn('text-[11px] font-bold', isCancelledH ? 'text-red-400 line-through' : isFutureH ? 'text-[#0288D1]' : 'text-slate-700')}>{dateLabel}</div>
                            {/* Total for this session (paid + agreed unpaid) */}
                            {sessionTotal > 0 ? (
                              <div className="text-[9px] font-bold text-[#26A69A] mb-0.5">{Math.round(sessionTotal * 10) / 10} CHF</div>
                            ) : (
                              <div className="text-[9px] text-slate-200 mb-0.5">—</div>
                            )}
                            {/* v2.29 : pas d'actions de séance pour l'assistante (le serveur les refuse) */}
                            {(amountsView || user?.role !== 'admin') ? (<><div className="lg:hidden h-7" /><div className="hidden lg:block h-4" /></>) : (<>
                            {/* Mobile : un seul bouton, les actions s'ouvrent en bas de l'écran */}
                            <button onClick={() => setSessionMenu(s)}
                              className="lg:hidden mx-auto w-9 h-7 rounded-lg flex items-center justify-center bg-white border border-slate-200 text-slate-500 active:bg-slate-100"
                              aria-label="Actions de la séance" data-testid={`session-menu-${s.id}`}>
                              <MoreHorizontal size={16} />
                            </button>
                            <div className="hidden lg:flex items-center justify-center gap-0.5">
                              {!isCancelledH && (
                                <button onClick={() => setSelectedSession(sessions.find(ss => ss.id === s.id))}
                                  className="w-5 h-4 rounded flex items-center justify-center hover:bg-slate-200" title="Modifier">
                                  <Edit2 size={9} className="text-slate-300 hover:text-slate-600" />
                                </button>
                              )}
                              <button onClick={async () => {
                                if (!window.confirm(isCancelledH ? 'Réactiver ce cours ?' : 'Annuler ce cours ?')) return;
                                await api.put(`/classes/${s.id}`, { status: isCancelledH ? 'active' : 'cancelled' }); fetchData();
                              }} className="w-5 h-4 rounded flex items-center justify-center hover:bg-slate-200" title={isCancelledH ? 'Réactiver' : 'Annuler'}>
                                <Ban size={9} className={isCancelledH ? 'text-green-500' : 'text-red-300 hover:text-red-500'} />
                              </button>
                              {!isCancelledH && (
                                <button onClick={async () => {
                                  if (isCompletedH) { await api.put(`/classes/${s.id}`, { status: 'active' }); fetchData(); return; }
                                  if (isFutureH && !window.confirm('Marquer comme terminée ?')) return;
                                  await api.put(`/classes/${s.id}`, { status: 'completed' }); fetchData();
                                }} className="w-5 h-4 rounded flex items-center justify-center hover:bg-slate-200" title={isCompletedH ? 'Remettre en attente' : 'Terminer'}>
                                  <CheckCheck size={9} className={isCompletedH ? 'text-[#26A69A]' : 'text-slate-300 hover:text-[#26A69A]'} />
                                </button>
                              )}
                            </div>
                            </>)}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {renderGroupHeader(followers, 'Followers', 'text-pink-500', 'bg-pink-50/40', 'followers')}
                    {!collapsedGroups.has('followers') && renderRows(followers)}
                    {renderGroupHeader(leaders, 'Leaders', 'text-[#0288D1]', 'bg-[#0288D1]/5', 'leaders')}
                    {!collapsedGroups.has('leaders') && renderRows(leaders)}
                    {unassigned.length > 0 && (<>
                      {renderGroupHeader(unassigned, 'Sans rôle', 'text-slate-500', 'bg-slate-50', 'unassigned')}
                      {!collapsedGroups.has('unassigned') && renderRows(unassigned)}
                    </>)}
                    {inactive.length > 0 && (<>
                      {renderGroupHeader(inactive, 'Inactifs', 'text-slate-400', 'bg-slate-100/60', 'inactive')}
                      {!collapsedGroups.has('inactive') && renderRows(inactive)}
                    </>)}
                    {enrolledStudents.length === 0 && (
                      <tr>
                        <td colSpan={sessionsSortedAsc.length + 2} className="text-center py-8 text-slate-400 text-sm">
                          Aucun élève inscrit
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              </div>
            );
          })()}
        </div>

      {/* EnrollModal, CheckoutSheet, etc. */}

      {showCheckout && (
        <CheckoutSheet
          sessions={sessions}
          course={course}
          enrolledStudents={enrolledStudents}
          coursePayments={coursePayments}
          packagesByStudent={packagesByStudent}
          onClose={() => setShowCheckout(false)}
          onPaid={fetchData}
        />
      )}
      {showGenerateModal && (
        <GenerateSlotsModal course={course} onClose={() => setShowGenerateModal(false)}
          onGenerated={() => { setShowGenerateModal(false); fetchData(); }} />
      )}
      {showEnrollModal && (
        <EnrollModal unenrolled={unenrolledStudents} courseId={id} isAdmin={user?.role === 'admin'}
          onClose={() => setShowEnrollModal(false)}
          onSaved={() => { setShowEnrollModal(false); fetchData(); }} />
      )}
      {showAddSession && (
        <ClassModal classData={null} prefilledData={prefilledSession} locations={locations} allStudents={allStudents}
          onClose={() => setShowAddSession(false)} onSaved={() => { setShowAddSession(false); fetchData(); }} />
      )}
      {selectedSession && (
        <ClassModal classData={selectedSession} locations={locations} allStudents={allStudents}
          onClose={() => setSelectedSession(null)} onSaved={() => { setSelectedSession(null); fetchData(); }} />
      )}
      {sessionMenu && (() => {
        const s = sessionMenu;
        const isCancelled = s.is_cancelled || s.status === 'cancelled';
        const isCompleted = s.is_completed || s.status === 'completed';
        const isFuture = !s.is_past && !isCompleted;
        const dateLabel = (() => { try { return format(parseISO(s.datetime_start), 'EEEE d MMMM', { locale }); } catch { return ''; } })();
        const close = () => setSessionMenu(null);
        const setStatus = async (status) => { close(); await api.put(`/classes/${s.id}`, { status }); fetchData(); };
        const actionCls = 'w-full flex items-center gap-3 px-4 h-14 rounded-xl text-base font-semibold active:bg-slate-100';
        return (
          <div className="fixed inset-0 z-50 flex items-end lg:hidden" onClick={close}>
            <div className="absolute inset-0 bg-black/30" />
            <div className="relative w-full bg-white rounded-t-3xl p-3 shadow-2xl" onClick={e => e.stopPropagation()}
              style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }} data-testid="session-menu-sheet">
              <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-3" />
              <div className="px-4 pb-2 text-sm font-semibold text-slate-500 capitalize">{dateLabel}</div>
              {!isCancelled && (
                <button className={cn(actionCls, 'text-slate-700')} onClick={() => { close(); setSelectedSession(sessions.find(ss => ss.id === s.id)); }}>
                  <Edit2 size={20} className="text-slate-400" /> Modifier la séance
                </button>
              )}
              {!isCancelled && (
                <button className={cn(actionCls, isCompleted ? 'text-slate-700' : 'text-[#26A69A]')}
                  onClick={() => { if (!isCompleted && isFuture && !window.confirm('Marquer comme terminée ?')) return; setStatus(isCompleted ? 'active' : 'completed'); }}>
                  <CheckCheck size={20} /> {isCompleted ? 'Remettre en attente' : 'Marquer comme terminée'}
                </button>
              )}
              <button className={cn(actionCls, isCancelled ? 'text-green-600' : 'text-red-500')}
                onClick={() => { if (!window.confirm(isCancelled ? 'Réactiver ce cours ?' : 'Annuler ce cours ?')) return; setStatus(isCancelled ? 'active' : 'cancelled'); }}>
                <Ban size={20} /> {isCancelled ? 'Réactiver la séance' : 'Annuler la séance'}
              </button>
              <button className="w-full h-12 mt-2 rounded-xl bg-slate-100 text-slate-600 font-semibold" onClick={close}>Fermer</button>
            </div>
          </div>
        );
      })()}
      {showEditCourse && (
        <GroupCourseModal course={course} locations={locations}
          onClose={() => setShowEditCourse(false)}
          onSaved={() => { setShowEditCourse(false); fetchData(); }} />
      )}
    </div>
  );
}

/* ── InlineControlMenu — popover 2-étapes via React Portal (bypass overflow) ── */
function InlineControlMenu({ trigger, options, onSelect, testId }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // 'click' (pas 'mousedown') pour éviter que le portal soit démonté
    // AVANT que le click sur une option puisse se déclencher
    const close = (e) => { if (!triggerRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  const handleClick = (e) => {
    e.stopPropagation();
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 2, left: rect.left });
    }
    setOpen(v => !v);
  };

  return (
    <>
      <button
        ref={triggerRef}
        onClick={handleClick}
        data-testid={testId}
        className="text-[12px] font-bold leading-none w-full text-center focus:outline-none cursor-pointer select-none"
      >
        {trigger}
      </button>
      {open && createPortal(
        <div
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999, minWidth: 100 }}
          className="bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden"
        >
          {options.map(opt => (
            <button
              key={opt.value}
              onClick={(e) => { e.stopPropagation(); onSelect(opt.value); setOpen(false); }}
              className={cn(
                'block w-full px-3 py-2 text-xs text-left transition-colors',
                opt.active ? 'font-bold text-[#0288D1] bg-[#0288D1]/5' : 'text-slate-600 hover:bg-slate-50'
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

/* ── BillingStatusDropdown — profil tarifaire par élève (Niveau 2) ── */
function BillingStatusDropdown({ courseId, studentId, currentStatus, pricingMatrix, onSaved }) {
  const [saving, setSaving] = useState(false);
  const options = pricingMatrix.length > 0 ? pricingMatrix : [{ id: 'std', status_name: 'Standard', status_code: null }];

  const handleChange = async (e) => {
    e.stopPropagation();
    const newStatus = e.target.value;
    setSaving(true);
    try {
      await api.put(`/group-courses/${courseId}/students/${studentId}`, { billing_status: newStatus });
      onSaved();
    } catch { toast.error('Erreur sauvegarde profil tarifaire'); }
    finally { setSaving(false); }
  };

  const isDefault = !currentStatus || currentStatus === 'Standard';
  // Display compact code in option text
  const getLabel = (row) => row.status_code ? `${row.status_code} – ${row.status_name}` : row.status_name;
  const currentRow = options.find(r => r.status_name === currentStatus);
  const displayValue = currentRow?.status_code || currentStatus?.slice(0, 2)?.toUpperCase() || '--';

  return (
    <select
      value={currentStatus || 'Standard'}
      onChange={handleChange}
      disabled={saving}
      onClick={e => e.stopPropagation()}
      title={currentStatus || 'Standard'}
      data-testid={`billing-status-${studentId}`}
      className={cn(
        'h-4 px-1 rounded-full border text-[9px] font-bold focus:outline-none focus:border-[#0288D1] cursor-pointer transition-colors',
        isDefault
          ? 'bg-slate-50 border-slate-200 text-slate-400'
          : 'bg-orange-50 border-orange-200 text-orange-600'
      )}
      style={{ width: `${Math.max(28, displayValue.length * 7 + 10)}px` }}
    >
      {options.map(row => (
        <option key={row.id || row.status_name} value={row.status_name}>{getLabel(row)}</option>
      ))}
    </select>
  );
}

/* ── EnrollStatusDropdown — statut actif/inactif par élève ── */
function EnrollStatusDropdown({ courseId, studentId, isActive, onSaved }) {
  const [saving, setSaving] = useState(false);

  const handleChange = async (e) => {
    e.stopPropagation();
    const newStatus = e.target.value;
    setSaving(true);
    try {
      await api.put(`/group-courses/${courseId}/students/${studentId}`, { status: newStatus });
      onSaved();
    } catch { toast.error('Erreur mise à jour statut'); }
    finally { setSaving(false); }
  };

  return (
    <select
      value={isActive ? 'active' : 'inactive'}
      onChange={handleChange}
      disabled={saving}
      onClick={e => e.stopPropagation()}
      data-testid={`enroll-status-select-${studentId}`}
      className={cn(
        'h-4 px-1 rounded-full border text-[9px] font-bold focus:outline-none cursor-pointer transition-colors shrink-0',
        isActive
          ? 'bg-slate-50 border-slate-200 text-slate-400'
          : 'bg-amber-50 border-amber-200 text-amber-600'
      )}
    >
      <option value="active">Actif</option>
      <option value="inactive">Inactif</option>
    </select>
  );
}

/* ── StudentPriceTag — prix personnalisé par élève (Niveau 2 — non affiché en grille) ── */
function StudentPriceTag({ courseId, studentId, customPrice, defaultPrice, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const inputRef = useRef(null);

  const openEdit = (e) => {
    e.stopPropagation();
    setVal(customPrice != null ? String(customPrice) : String(defaultPrice || ''));
    setEditing(true);
    setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select(); }, 30);
  };

  const save = async () => {
    const num = parseFloat(val);
    if (isNaN(num) || num <= 0) { setEditing(false); return; }
    try {
      // Send -1 to clear override if same as default
      await api.put(`/group-courses/${courseId}/students/${studentId}`, {
        price_per_class: num,
      });
      onSaved();
    } catch { toast.error('Erreur sauvegarde prix'); }
    setEditing(false);
  };

  const clear = async (e) => {
    e.stopPropagation();
    try {
      await api.put(`/group-courses/${courseId}/students/${studentId}`, { price_per_class: -1 });
      onSaved();
    } catch { toast.error('Erreur'); }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-0.5 mt-0.5" onClick={e => e.stopPropagation()}>
        <input ref={inputRef} type="number" value={val} onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          onBlur={save}
          className="w-14 h-5 px-1 text-[10px] border rounded border-[#0288D1] focus:outline-none text-center bg-white" />
        <span className="text-[9px] text-slate-400">CHF</span>
      </div>
    );
  }

  const isCustom = customPrice != null;
  const display = customPrice ?? defaultPrice;

  return (
    <button onClick={openEdit} data-testid={`price-tag-${studentId}`}
      className={cn(
        'mt-0.5 flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[9px] font-bold transition-colors h-4',
        isCustom
          ? 'bg-orange-50 border border-orange-200 text-orange-600 hover:bg-orange-100'
          : 'bg-slate-50 border border-slate-200 text-slate-400 hover:bg-slate-100'
      )}>
      {display != null ? `${display}.-` : '—'}
      {isCustom && (
        <span onMouseDown={clear} className="ml-0.5 text-orange-400 hover:text-orange-700 font-bold leading-none">×</span>
      )}
    </button>
  );
}

/* ── QuickCashPayBtn ── montant cliquable + édition inline ── */
function QuickCashPayBtn({ studentId, courseId, sessionDate, defaultAmount, onPaid, existingPayment, pill = false }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const handleOpen = () => {
    setAmount(existingPayment ? String(existingPayment.amount) : (defaultAmount ? String(defaultAmount) : '25'));
    setOpen(true);
    setTimeout(() => inputRef.current?.select(), 50);
  };

  const handleSave = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) return;
    setSaving(true);
    try {
      if (existingPayment) {
        await api.put(`/payments/${existingPayment.id}`, { amount: val });
      } else {
        await api.post('/payments', {
          student_id: studentId, amount: val, date: sessionDate,
          method: 'cash', notes: 'Paiement séance collective',
          package_id: null, group_course_id: courseId,
        });
      }
      toast.success(`${val} CHF enregistré`);
      setOpen(false);
      onPaid();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!existingPayment) return;
    setSaving(true);
    try {
      await api.delete(`/payments/${existingPayment.id}`);
      toast.success('Paiement supprimé');
      setOpen(false);
      onPaid();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  if (open) {
    return (
      <div className="flex items-center gap-0.5" onKeyDown={e => e.key === 'Escape' && setOpen(false)}>
        <input ref={inputRef} type="number" value={amount} onChange={e => setAmount(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSave()}
          className="w-11 text-[10px] border border-green-300 rounded px-1 py-0.5 text-center focus:outline-none focus:ring-1 focus:ring-green-400"
          min="0" step="0.5" autoFocus data-testid="quick-cash-input" />
        <button onClick={handleSave} disabled={saving} className="text-green-500 hover:text-green-700 font-bold text-[11px]" title="Sauvegarder">✓</button>
        {existingPayment && <button onClick={handleDelete} disabled={saving} className="text-red-400 hover:text-red-600 text-[11px] font-bold" title="Supprimer">🗑</button>}
        <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600 text-[11px]" title="Annuler">✗</button>
      </div>
    );
  }

  if (existingPayment) {
    return (
      <button onClick={handleOpen} data-testid="quick-cash-amount"
        className={cn(
          'text-[10px] font-bold leading-none transition-colors cursor-pointer',
          pill
            ? 'text-[#26A69A] hover:opacity-70 px-1.5'
            : 'text-green-600 bg-green-50 border border-green-200 px-1.5 rounded hover:bg-green-100'
        )}>
        {existingPayment.amount}.-
      </button>
    );
  }

  return (
    <button onClick={handleOpen} data-testid="quick-cash-btn"
      className={cn(
        'text-[10px] font-medium leading-none transition-colors',
        pill
          ? 'text-[#26A69A]/40 hover:text-[#26A69A] px-1.5'
          : 'text-slate-300 hover:text-green-500 hover:bg-green-50 px-1 rounded'
      )}>
      CHF
    </button>
  );
}

/* ── AttendanceCycleBtn ── bouton unique cyclant : Absent → Présent → Essai → Absent */
function AttendanceCycleBtn({ classId, studentId, att, onChanged, pill = false, classDate, userRole, isRecentSession = false }) {
  const getState = (a) => {
    if (!a) return 'absent';
    if (a.is_not_enrolled) return 'not_enrolled';
    if (a.is_aide) return 'present';  // legacy aide → treated as present for display
    if (a.is_trial) return 'trial';
    if (a.present) return 'present';
    return 'absent';
  };
  const [state, setState] = useState(() => getState(att));
  const [saving, setSaving] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const canEdit = userRole === 'admin'
    || (userRole === 'assistant' && (isRecentSession || classDate === today));

  const CYCLE = { absent: 'present', present: 'trial', trial: 'absent', not_enrolled: 'absent' };
  const CFG = {
    absent:       { label: 'A',   cls: 'bg-red-50 text-red-400 border-red-100',               title: 'Absent' },
    present:      { label: 'P',   cls: 'bg-[#26A69A]/10 text-[#26A69A] border-[#26A69A]/20', title: 'Présent' },
    trial:        { label: 'E',   cls: 'bg-orange-50 text-orange-500 border-orange-200',       title: 'Essai' },
    not_enrolled: { label: 'N/I', cls: 'bg-slate-100 text-slate-400 border-slate-200',         title: 'Non inscrit' },
  };
  const toAtt = (s) => ({
    present: s === 'present' || s === 'trial',
    is_trial: s === 'trial',
    is_aide: false,
    is_not_enrolled: s === 'not_enrolled',
  });
  const cycle = async () => {
    if (!canEdit) return;
    const next = CYCLE[state] ?? 'absent';
    setState(next);
    setSaving(true);
    try {
      await api.put(`/classes/${classId}/attendance`, [{ student_id: studentId, ...toAtt(next) }]);
      onChanged?.();
    } catch { setState(getState(att)); }
    finally { setSaving(false); }
  };
  const cfg = CFG[state] ?? CFG.absent;
  const disabledTitle = !canEdit ? 'Modification autorisée uniquement le jour du cours' : cfg.title;
  if (pill) {
    return (
      <button onClick={cycle} disabled={saving || !canEdit} title={disabledTitle}
        className={cn('px-2 h-full text-xs font-bold text-[#26A69A] transition-all bg-transparent',
          canEdit ? 'hover:opacity-70' : 'opacity-30 cursor-not-allowed',
          saving && 'opacity-50 cursor-wait')}
        data-testid={`att-cycle-${studentId}`}>
        {saving ? '…' : cfg.label}
      </button>
    );
  }
  return (
    <button onClick={cycle} disabled={saving || !canEdit} title={disabledTitle}
      className={cn('w-16 h-7 rounded-full text-xs font-bold border flex items-center justify-center transition-all',
        cfg.cls,
        canEdit ? 'hover:opacity-80' : 'opacity-30 cursor-not-allowed',
        saving && 'opacity-50 cursor-wait')}
      data-testid={`att-cycle-${studentId}`}>
      {saving ? '…' : cfg.label}
    </button>
  );
}

/* ── StudentStatusButtons ── independent status buttons: Active/Inactive + Trial + Aide + N/I */
function StudentStatusButtons({ studentId, courseId, currentRole, currentStatus, onChanged }) {
  const status = currentStatus || 'active';
  const [saving, setSaving] = useState(false);

  const updateStatus = async (e, newStatus) => {
    e.stopPropagation();
    if (saving) return;
    setSaving(true);
    try {
      await api.post(`/group-courses/${courseId}/students`, {
        student_id: studentId,
        role: currentRole || '',
        status: newStatus,
      });
      onChanged();
    } finally { setSaving(false); }
  };

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={(e) => updateStatus(e, status === 'active' ? 'inactive' : 'active')}
        disabled={saving}
        className={cn('px-2 py-1 rounded-full text-xs font-bold transition-all',
          status === 'active' ? 'bg-[#26A69A]/10 text-[#26A69A]' : 'bg-slate-100 text-slate-500')}
        data-testid={`status-active-${studentId}`}>
        {status === 'active' ? 'Actif' : 'Inactif'}
      </button>
      <button
        onClick={(e) => updateStatus(e, status === 'trial' ? 'active' : 'trial')}
        disabled={saving}
        className={cn('px-2 py-1 rounded-full text-xs font-bold transition-all border',
          status === 'trial' ? 'bg-orange-50 text-orange-500 border-orange-200' : 'bg-white text-slate-300 border-slate-100 hover:border-orange-200 hover:text-orange-400')}
        data-testid={`status-trial-${studentId}`}>
        Essai
      </button>
      <button
        onClick={(e) => updateStatus(e, status === 'aide' ? 'active' : 'aide')}
        disabled={saving}
        className={cn('px-2 py-1 rounded-full text-xs font-bold transition-all border',
          status === 'aide' ? 'bg-purple-50 text-purple-500 border-purple-200' : 'bg-white text-slate-300 border-slate-100 hover:border-purple-200 hover:text-purple-400')}
        title="Aide (vient mais ne paie pas)"
        data-testid={`status-aide-${studentId}`}>
        Aide
      </button>
      <button
        onClick={(e) => updateStatus(e, status === 'not_enrolled' ? 'active' : 'not_enrolled')}
        disabled={saving}
        className={cn('px-2 py-1 rounded-full text-xs font-bold transition-all border',
          status === 'not_enrolled' ? 'bg-slate-200 text-slate-600 border-slate-300' : 'bg-white text-slate-200 border-slate-100 hover:border-slate-300 hover:text-slate-500')}
        title="Non inscrit"
        data-testid={`status-not-enrolled-${studentId}`}>
        N/I
      </button>
    </div>
  );
}

/* ── RoleSelector ── cycles leader / follower / ? */
function RoleSelector({ studentId, courseId, currentRole, currentStatus, onChanged }) {
  const roles = [
    { value: 'leader',   label: 'L', color: 'bg-[#0288D1] text-white' },
    { value: 'follower', label: 'F', color: 'bg-pink-400 text-white' },
    { value: '',         label: '?', color: 'bg-slate-200 text-slate-600' },
  ];
  const current = roles.find(r => r.value === currentRole) || roles[2];
  const next = roles[(roles.indexOf(current) + 1) % roles.length];

  const toggle = async (e) => {
    e.stopPropagation();
    await api.post(`/group-courses/${courseId}/students`, {
      student_id: studentId,
      role: next.value,
      status: currentStatus || 'active',
    });
    onChanged();
  };

  return (
    <button
      onClick={toggle}
      className={cn('w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all', current.color)}
      title={`Rôle: ${current.value || 'non spécifié'}`}
      data-testid={`role-btn-${studentId}`}
    >
      {current.label}
    </button>
  );
}

/* ── GenerateSlotsModal ── */
function GenerateSlotsModal({ course, onClose, onGenerated }) {
  const DAYS_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
  const today = format(new Date(), 'yyyy-MM-dd');
  const defaultEnd = format(addMonths(new Date(), 6), 'yyyy-MM-dd');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  // Preview: count occurrences
  const expectedCount = (() => {
    try {
      let d = new Date(startDate + 'T00:00:00');
      const end = new Date(endDate + 'T23:59:59');
      const jsDow = (course.day_of_week + 1) % 7; // app 0=Mon → JS 1=Mon
      let n = 0;
      while (d <= end && n < 500) { if (d.getDay() === jsDow) n++; d.setDate(d.getDate() + 1); }
      return n;
    } catch { return 0; }
  })();

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/group-courses/${course.id}/generate-slots`, { start_date: startDate, end_date: endDate });
      setResult(res);
      onGenerated();
    } catch {
      toast.error('Erreur lors de la génération');
    } finally { setGenerating(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-0 md:p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full md:max-w-sm rounded-t-3xl md:rounded-2xl shadow-2xl" data-testid="generate-slots-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <div className="font-bold text-slate-900">Générer les séances</div>
            <div className="text-xs text-slate-400 mt-0.5">{DAYS_FR[course.day_of_week]} · {course.start_time} · {Math.floor(course.duration_minutes/60)}h{course.duration_minutes%60||''}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors">
            <XCircle size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {result ? (
            <div className="text-center py-4">
              <CheckCircle size={40} className="text-[#26A69A] mx-auto mb-3" />
              <div className="text-lg font-bold text-slate-900">{result.created} séances créées</div>
              <div className="text-sm text-slate-500 mt-1">{result.skipped > 0 && `${result.skipped} déjà existantes ignorées`}</div>
              <button onClick={onClose} className="mt-4 w-full h-11 bg-[#0288D1] text-white rounded-xl font-bold text-sm">Fermer</button>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Date de début</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                  className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20"
                  data-testid="gen-start-date" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Date de fin</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                  className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20"
                  data-testid="gen-end-date" />
              </div>
              {expectedCount > 0 && (
                <div className="bg-[#0288D1]/5 rounded-xl px-4 py-3 flex items-center justify-between border border-[#0288D1]/20">
                  <span className="text-sm text-slate-600">Séances à créer</span>
                  <span className="text-lg font-extrabold text-[#0288D1] font-manrope">{expectedCount}</span>
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <button onClick={onClose} className="flex-1 h-11 rounded-xl bg-slate-100 text-slate-700 font-bold text-sm">Annuler</button>
                <button onClick={handleGenerate} disabled={generating || expectedCount === 0}
                  className="flex-1 h-11 rounded-xl bg-[#0288D1] text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-generate-btn">
                  {generating ? <><Repeat size={15} className="animate-spin" /> Génération...</> : `Générer ${expectedCount} séances`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── BillingBadge — shows per-session billing with inline price override ── */
function BillingBadge({ billing, studentId, enrollmentStatus, courseId, onRefresh }) {
  const navigate = useNavigate();
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceVal, setPriceVal] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);

  if (enrollmentStatus === 'not_enrolled') {
    return <span className="text-xs text-slate-400 px-2 py-1 bg-slate-50 rounded-lg border border-slate-100">N/I</span>;
  }
  if (enrollmentStatus === 'trial' || enrollmentStatus === 'aide') {
    return <span className={cn('text-xs px-2 py-1 rounded-lg border',
      enrollmentStatus === 'aide' ? 'text-purple-500 bg-purple-50 border-purple-100' : 'text-orange-500 bg-orange-50 border-orange-100')}>
      {enrollmentStatus === 'aide' ? 'Aide' : 'Essai'}
    </span>;
  }
  if (!billing) return null;

  const { present_count, amount_owed, paid_amount, balance, price_per_class, price_override } = billing;
  const isCredit = balance < 0;
  const isPaid = balance <= 0;

  const savePrice = async () => {
    setSavingPrice(true);
    try {
      const val = parseFloat(priceVal);
      await api.put(`/group-courses/${courseId}/students/${studentId}`, {
        price_per_class: isNaN(val) || priceVal === '' ? -1 : val,
      });
      setEditingPrice(false);
      onRefresh();
    } finally { setSavingPrice(false); }
  };

  return (
    <div className="flex-1 space-y-1.5">
      {/* Price per class (editable) */}
      <div className="flex items-center gap-1 text-[11px] text-slate-500">
        <span>Prix:</span>
        {editingPrice ? (
          <input
            autoFocus
            type="number"
            value={priceVal}
            onChange={e => setPriceVal(e.target.value)}
            onBlur={savePrice}
            onKeyDown={e => { if (e.key === 'Enter') savePrice(); if (e.key === 'Escape') setEditingPrice(false); }}
            className="w-16 px-1 py-0.5 border border-amber-400 rounded text-xs outline-none bg-amber-50"
            placeholder={String(price_per_class)}
          />
        ) : (
          <button
            onClick={() => { setPriceVal(String(price_override ?? '')); setEditingPrice(true); }}
            className={cn('font-bold hover:text-amber-600 transition-colors', price_override ? 'text-amber-600' : 'text-slate-600')}
            title="Cliquer pour modifier le prix de ce cours"
            data-testid={`price-override-${studentId}`}
          >
            {price_per_class} CHF {price_override ? '✎' : ''}
          </button>
        )}
        <span className="text-slate-400">· {present_count} ×</span>
      </div>
      {/* Progress bar */}
      <div>
        <div className="flex justify-between text-[10px] mb-0.5">
          <span className={cn('font-semibold', isPaid ? 'text-[#26A69A]' : 'text-red-500')}>
            {isCredit ? `Crédit: ${Math.abs(balance)} CHF` : isPaid ? 'Soldé' : `Doit ${balance} CHF`}
          </span>
          <button onClick={(e) => { e.stopPropagation(); navigate(`/students/${studentId}`); }}
            className="text-slate-400 hover:text-[#0288D1] transition-colors">
            {paid_amount}/{amount_owed} CHF
          </button>
        </div>
        {amount_owed > 0 && (
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-[#26A69A] rounded-full transition-all"
              style={{ width: `${Math.min(100, (paid_amount / amount_owed) * 100)}%` }} />
          </div>
        )}
      </div>
    </div>
  );
}

/* ── StudentEnrollCard ── compact card for Leaders/Followers columns */
function StudentEnrollCard({ student, role, student_id, status, billing, courseId, onRefresh, onUnenroll }) {
  const navigate = useNavigate();
  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-2.5" data-testid="enrolled-student-row">
      <div className="flex items-center justify-between gap-1 mb-1.5">
        <button
          onClick={() => navigate(`/students/${student_id}`)}
          className="flex items-center gap-2 hover:opacity-80 min-w-0 flex-1"
        >
          <Avatar name={`${student.first_name} ${student.last_name}`} size="sm" />
          <span className="text-sm font-semibold text-slate-900 truncate">{student.first_name} {student.last_name}</span>
        </button>
        <button
          onClick={() => onUnenroll(student_id)}
          className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors shrink-0"
          data-testid="unenroll-btn"
        >
          <Trash2 size={12} className="text-red-400" />
        </button>
      </div>
      <div className="space-y-1.5">
        <BillingBadge billing={billing} studentId={student_id} enrollmentStatus={status} courseId={courseId} onRefresh={onRefresh} />
        <div className="flex items-center gap-1 flex-wrap">
          <StudentStatusButtons studentId={student_id} courseId={courseId} currentRole={role} currentStatus={status} onChanged={onRefresh} />
          <RoleSelector studentId={student_id} courseId={courseId} currentRole={role} currentStatus={status} onChanged={onRefresh} />
        </div>
      </div>
    </div>
  );
}

/* ── EnrollModal ── */
function EnrollModal({ unenrolled, courseId, onClose, onSaved, isAdmin = true }) {
  const { t } = useLang();
  const [createPrefill, setCreatePrefill] = useState(null);  // v2.29 : « Créer » ouvre la fiche élève normale (pas de 2e formulaire)
  const [created, setCreated] = useState([]);                // élèves créés ici, ajoutés à la liste et cochés
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [role, setRole] = useState('');
  const [filterRole, setFilterRole] = useState(''); // '', 'leader', 'follower'
  const [status, setStatus] = useState('active');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = [...created, ...unenrolled].filter(s => {
    const t = search.toLowerCase().trim();
    const ph = (s.phone || '').replace(/\D/g, '');
    const st = t.replace(/\D/g, '');
    const matchName = !t ||
      `${s.first_name} ${s.last_name}`.toLowerCase().includes(t) ||
      (s.instagram || '').toLowerCase().includes(t) ||
      (st.length >= 4 && ph.includes(st));
    const matchRole = !filterRole || s.role === filterRole;
    return matchName && matchRole;
  });

  const toggleId = (id) => setSelectedIds(ids => {
    const n = new Set(ids);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const handleShowCreate = () => {
    const parts = search.trim().split(' ');
    setCreatePrefill({ first_name: parts[0] || '', last_name: parts.slice(1).join(' ') || '' });
  };
  const handleCreated = (st) => {
    setCreatePrefill(null);
    if (!st?.id) return;
    setCreated(c => [st, ...c]);
    setSelectedIds(ids => new Set([...ids, st.id]));
    setSearch('');
    if (st.role) setRole(st.role);
  };

  const handleEnroll = async () => {
    if (selectedIds.size === 0) return;
    setSaving(true);
    try {
      await Promise.all([...selectedIds].map(sid =>
        api.post(`/group-courses/${courseId}/students`, { student_id: sid, role, status })
      ));
      toast.success(selectedIds.size > 1 ? `${selectedIds.size} élèves inscrits` : 'Élève inscrit');
      onSaved();
    } catch { toast.error('Erreur lors de l\'inscription'); }
    finally { setSaving(false); }
  };


  const RoleStatusPickers = () => (
    <>
      <div>
        <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Rôle</label>
        <div className="flex gap-2">
          {[['leader','Leader','bg-[#0288D1]'],['follower','Follower','bg-pink-400'],['','?','bg-slate-400']].map(([val, label, color]) => (
            <button key={val} onClick={() => setRole(val)}
              className={cn('flex-1 h-9 rounded-xl text-sm font-bold text-white transition-all', color, role !== val && 'opacity-40')}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {!isAdmin ? (
        <div className="text-xs font-semibold text-orange-600 bg-orange-50 rounded-xl px-3 py-2" data-testid="enroll-trial-note">
          Ajouté(e) en essai [E] — Thierry verra « ajouté par toi »
        </div>
      ) : (
      <div>
        <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Statut</label>
        <div className="flex gap-2 flex-wrap">
          {[['active','Actif','bg-[#26A69A]'],['trial','Essai','bg-orange-400'],['aide','Aide','bg-purple-500'],['inactive','Inactif','bg-slate-400'],['not_enrolled','N/I','bg-slate-500']].map(([val, label, color]) => (
            <button key={val} onClick={() => setStatus(val)}
              className={cn('flex-1 h-9 rounded-xl text-sm font-bold text-white transition-all', color, status !== val && 'opacity-40')}
              data-testid={`status-option-${val}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      )}
    </>
  );

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-0 md:p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full md:max-w-sm rounded-t-3xl md:rounded-2xl shadow-2xl" data-testid="enroll-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-bold text-lg text-slate-900 font-manrope">
            {t('groupClasses.addStudent')}
          </h2>
          <div className="flex items-center gap-2">
            {(
              <button onClick={handleShowCreate} data-testid="create-new-student-btn"
                className="flex items-center gap-1.5 px-3 h-8 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold hover:bg-emerald-100 transition-colors">
                <Plus size={13} /> Créer
              </button>
            )}
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100">
              <XCircle size={16} className="text-slate-500" />
            </button>
          </div>
        </div>

        {(
          <div className="px-5 py-4 space-y-4">
            {/* L/F filter + search */}
            <div className="flex gap-2 items-center">
              <div className="flex gap-1 shrink-0">
                {[['', 'Tous'], ['follower', 'F'], ['leader', 'L']].map(([val, label]) => (
                  <button key={val} onClick={() => setFilterRole(val)}
                    className={cn('px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-colors',
                      filterRole === val
                        ? val === 'follower' ? 'bg-pink-500 text-white border-pink-500'
                          : val === 'leader' ? 'bg-[#0288D1] text-white border-[#0288D1]'
                          : 'bg-slate-700 text-white border-slate-700'
                        : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                    )}
                    data-testid={`enroll-filter-${val || 'all'}`}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="relative flex-1">
                <input type="text" value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Rechercher un élève..."
                  autoFocus data-testid="enroll-search-input"
                  className="w-full h-10 pl-9 pr-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0288D1]/30 focus:border-[#0288D1]"
                />
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
              </div>
            </div>
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-[#0288D1]/5 border border-[#0288D1]/20 rounded-xl">
                <CheckCircle size={14} className="text-[#0288D1]" />
                <span className="text-sm font-semibold text-[#0288D1] flex-1">{selectedIds.size} élève{selectedIds.size > 1 ? 's' : ''} sélectionné{selectedIds.size > 1 ? 's' : ''}</span>
                <button onClick={() => setSelectedIds(new Set())} className="text-slate-400 hover:text-slate-600 text-xs">Effacer</button>
              </div>
            )}
            <div className="max-h-44 overflow-y-auto border border-slate-100 rounded-xl divide-y divide-slate-100">
              {unenrolled.length + created.length === 0 ? (
                <p className="text-center py-6 text-slate-400 text-sm">Tous les élèves sont inscrits</p>
              ) : filtered.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-slate-400 text-sm mb-3">Aucun résultat pour "{search}"</p>
                  <button onClick={handleShowCreate} data-testid="create-from-search-btn"
                    className="inline-flex items-center gap-1.5 px-4 h-9 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-bold hover:bg-emerald-100 transition-colors">
                    <Plus size={14} /> Créer "{search}"
                  </button>
                </div>
              ) : (
                filtered.map(s => {
                  const isSelected = selectedIds.has(s.id);
                  return (
                    <button key={s.id} onClick={() => toggleId(s.id)}
                      className={cn('w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors', isSelected && 'bg-[#0288D1]/5')}
                      data-testid={`enroll-option-${s.id}`}>
                      <div className={cn('w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors', isSelected ? 'bg-[#0288D1] border-[#0288D1]' : 'border-slate-300')}>
                        {isSelected && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round"/></svg>}
                      </div>
                      <Avatar name={`${s.first_name} ${s.last_name}`} size="sm" />
                      <span className="text-sm font-medium text-slate-800 flex-1 text-left">{s.first_name} {s.last_name}</span>
                    </button>
                  );
                })
              )}
            </div>
            <RoleStatusPickers />
          </div>
        )}

        <div className="px-5 pb-5 flex gap-3">
          <button onClick={onClose} className="flex-1 h-12 rounded-xl bg-slate-100 text-slate-700 font-bold text-sm">Annuler</button>
          {(
            <button onClick={handleEnroll} disabled={selectedIds.size === 0 || saving}
              className="flex-1 h-12 rounded-xl bg-[#0288D1] text-white font-bold text-sm disabled:opacity-50"
              data-testid="confirm-enroll-btn">
              {saving ? '...' : selectedIds.size > 1 ? `Inscrire (${selectedIds.size})` : 'Inscrire'}
            </button>
          )}
        </div>
      </div>
      {createPrefill && <StudentModal student={createPrefill} onClose={() => setCreatePrefill(null)} onSaved={handleCreated} />}
    </div>
  );
}


/* ── CheckoutSheet — caisse fin de cours ── */
function CheckoutSheet({ sessions, course, enrolledStudents, coursePayments, packagesByStudent, onClose, onPaid }) {
  const _getLastMethod = () => { try { return localStorage.getItem('bachata_last_method') || 'cash'; } catch { return 'cash'; } };
  const _saveLastMethod = m => { try { localStorage.setItem('bachata_last_method', m); } catch {} };

  const availableSessions = [...sessions]
    .filter(s => s.is_past && s.status !== 'cancelled' && !s.is_cancelled)
    .sort((a, b) => b.datetime_start.localeCompare(a.datetime_start));

  const [selectedSessionId, setSelectedSessionId] = useState(availableSessions[0]?.id || null);
  const [method, setMethod] = useState(_getLastMethod());
  const [paying, setPaying] = useState(new Set());
  const [paid, setPaid] = useState(new Set());
  const [checkoutSearch, setCheckoutSearch] = useState('');
  const [checkoutRole, setCheckoutRole] = useState(''); // '', 'leader', 'follower'

  const selectedSession = availableSessions.find(s => s.id === selectedSessionId);

  const presentStudents = selectedSession ? enrolledStudents.filter(({ student_id }) => {
    const att = selectedSession.attendances?.find(a => a.student_id === student_id);
    if (!att?.present || att?.is_trial || att?.is_aide) return false;
    const studentPkgs = packagesByStudent[student_id] || [];
    return !studentPkgs.some(p => p.type === 'group' || p.type === 'mixed');
  }) : [];

  const matchFilter = ({ student, role }) => {
    const t = (checkoutSearch || '').toLowerCase().trim();
    const ph = (student.phone || '').replace(/\D/g, '');
    const st = t.replace(/\D/g, '');
    const matchSearch = !t ||
      `${student.first_name} ${student.last_name}`.toLowerCase().includes(t) ||
      (student.instagram || '').toLowerCase().includes(t) ||
      (st.length >= 4 && ph.includes(st));
    const matchRole = !checkoutRole || role === checkoutRole;
    return matchSearch && matchRole;
  };

  const unpaidStudents = presentStudents.filter(({ student_id }) => {
    if (paid.has(student_id)) return false;
    return !coursePayments.find(p => p.student_id === student_id && p.date === selectedSession?.datetime_start);
  }).filter(matchFilter);

  const alreadyPaid = presentStudents.filter(({ student_id }) => {
    if (paid.has(student_id)) return true;
    return coursePayments.some(p => p.student_id === student_id && p.date === selectedSession?.datetime_start);
  }).filter(matchFilter);

  const handlePay = async (studentId, billing) => {
    if (!selectedSession) return;
    const amount = billing?.price_per_class ?? course?.price_per_class ?? 20;
    setPaying(p => new Set([...p, studentId]));
    try {
      await api.post('/payments', {
        student_id: studentId,
        amount: parseFloat(amount),
        date: selectedSession.datetime_start,
        method,
        notes: '',
        package_id: null,
        group_course_id: course.id,
      });
      setPaid(p => new Set([...p, studentId]));
      toast.success(`✓ ${amount} CHF encaissé`);
      onPaid();
    } catch { toast.error('Erreur paiement'); }
    finally { setPaying(p => { const n = new Set(p); n.delete(studentId); return n; }); }
  };

  const handleMethodChange = m => { setMethod(m); _saveLastMethod(m); };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end justify-center"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full max-w-lg rounded-t-3xl shadow-2xl flex flex-col" style={{ maxHeight: '88dvh' }}
        data-testid="checkout-sheet">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <div>
            <div className="font-bold text-slate-900">Caisse fin de cours</div>
            {selectedSession && (
              <div className="text-xs text-slate-400">
                {(() => { try { return format(parseISO(selectedSession.datetime_start), 'dd/MM · HH:mm'); } catch { return ''; } })()}
                {' · '}
                {unpaidStudents.length > 0
                  ? <span className="text-red-500 font-semibold">{unpaidStudents.length} à encaisser</span>
                  : <span className="text-[#26A69A] font-semibold">Tout encaissé ✓</span>
                }
              </div>
            )}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200">
            <XCircle size={16} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {/* Session selector */}
          {availableSessions.length > 1 && (
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Séance</label>
              <select value={selectedSessionId || ''} onChange={e => setSelectedSessionId(e.target.value)}
                className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#26A69A]/20"
                data-testid="checkout-session-select">
                {availableSessions.map(s => (
                  <option key={s.id} value={s.id}>
                    {(() => { try { return format(parseISO(s.datetime_start), 'EEEE dd/MM · HH:mm', { locale: undefined }); } catch { return s.datetime_start; } })()}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Method selector */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Méthode</label>
            <div className="flex gap-2">
              {[['cash', 'Cash'], ['twint', 'Twint'], ['virement', 'Virement']].map(([val, label]) => (
                <button key={val} onClick={() => handleMethodChange(val)}
                  className={cn('flex-1 py-2.5 rounded-xl text-sm font-bold transition-colors',
                    method === val ? 'bg-[#26A69A] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}
                  data-testid={`checkout-method-${val}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Students to pay */}
          {!selectedSession ? (
            <div className="text-center py-8 text-slate-400 text-sm">Aucune séance disponible</div>
          ) : (presentStudents.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">Aucun élève présent à encaisser pour cette séance</div>
          ) : (
            <div className="space-y-2">
              {/* Search + L/F filter */}
              <div className="flex gap-2 items-center">
                <div className="flex gap-1 shrink-0">
                  {[['', 'Tous'], ['follower', 'F'], ['leader', 'L']].map(([val, label]) => (
                    <button key={val} onClick={() => setCheckoutRole(val)}
                      className={cn('px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-colors',
                        checkoutRole === val
                          ? val === 'follower' ? 'bg-pink-500 text-white border-pink-500'
                            : val === 'leader' ? 'bg-[#0288D1] text-white border-[#0288D1]'
                            : 'bg-slate-700 text-white border-slate-700'
                          : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                      )}
                      data-testid={`checkout-role-${val || 'all'}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <div className="relative flex-1">
                  <input value={checkoutSearch} onChange={e => setCheckoutSearch(e.target.value)}
                    placeholder="Filtrer par nom…" data-testid="checkout-search-input"
                    className="w-full h-9 pl-8 pr-3 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#26A69A]/30 focus:border-[#26A69A]"
                  />
                  <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                  </svg>
                </div>
              </div>
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">À encaisser · {unpaidStudents.length}</div>
              )}
              {unpaidStudents.map(({ student, student_id, billing }) => {
                const amount = billing?.price_per_class ?? course?.price_per_class ?? 20;
                const isProcessing = paying.has(student_id);
                return (
                  <div key={student_id} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-100 shadow-sm"
                    data-testid="checkout-student-row">
                    <Avatar name={`${student.first_name} ${student.last_name}`} size="sm" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-slate-900 truncate">{student.first_name} {student.last_name}</div>
                      <div className="text-xs text-slate-400">{amount} CHF · {method}</div>
                    </div>
                    <button onClick={() => handlePay(student_id, billing)}
                      disabled={isProcessing}
                      className="h-9 px-4 bg-[#26A69A] text-white font-bold text-sm rounded-xl active:scale-95 transition-all disabled:opacity-50 shrink-0"
                      data-testid={`checkout-pay-btn-${student_id}`}>
                      {isProcessing ? '…' : `✓ ${amount} CHF`}
                    </button>
                  </div>
                );
              })}

              {alreadyPaid.length > 0 && (
                <div className={cn('space-y-1.5', unpaidStudents.length > 0 && 'pt-2 border-t border-slate-100')}>
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Déjà encaissé · {alreadyPaid.length}</div>
                  {alreadyPaid.map(({ student, student_id, billing }) => {
                    const amount = billing?.price_per_class ?? course?.price_per_class ?? 20;
                    const existingPay = coursePayments.find(p => p.student_id === student_id && p.date === selectedSession.datetime_start);
                    return (
                      <div key={student_id} className="flex items-center gap-3 p-2.5 rounded-xl bg-[#26A69A]/5"
                        data-testid="checkout-paid-row">
                        <Avatar name={`${student.first_name} ${student.last_name}`} size="sm" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-600 truncate">{student.first_name} {student.last_name}</div>
                        </div>
                        <span className="text-sm font-bold text-[#26A69A] shrink-0">✓ {existingPay?.amount ?? amount} CHF</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="shrink-0 px-5 pb-6 pt-3 border-t border-slate-100">
          <button onClick={onClose}
            className="w-full h-12 rounded-2xl bg-slate-100 text-slate-700 font-bold text-base hover:bg-slate-200 transition-colors">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}


/* ── v2.23 : vue « Montants » — lecture seule, aucune saisie ──
   Chaque case = montant compté pour cet élève à cette séance ; la ligne verte = la somme (= montant vert du tableau). */
// v2.23.2 : bouton 🧮 à côté de « Élève » : ouvre la vue Montants / revient au tableau des présences
function AmountsToggle({ active, onClick }) {
  return (
    <button onClick={onClick} data-testid="amounts-toggle" title={active ? 'Revenir au tableau des présences' : 'Montants par élève (lecture seule)'}
      className={cn('w-6 h-6 rounded-md flex items-center justify-center normal-case',
        active ? 'bg-[#26A69A] text-white' : 'text-slate-400 hover:text-[#26A69A] hover:bg-[#26A69A]/10')}>
      <Calculator size={13} />
    </button>
  );
}

// v2.23.3 : même pastille que la présence (mêmes couleurs), le montant compté à la place de la lettre ; lecture seule
function AmountPill({ att, look, fmt }) {
  const state = !att ? 'absent' : att.is_not_enrolled ? 'not_enrolled' : att.is_aide ? 'present' : att.is_trial ? 'trial' : att.present ? 'present' : 'absent';
  const CFG = {
    absent:       { label: 'A',   cls: 'bg-red-50 text-red-400 border-red-100' },
    present:      { label: 'P',   cls: 'bg-[#26A69A]/10 text-[#26A69A] border-[#26A69A]/20' },
    trial:        { label: 'E',   cls: 'bg-orange-50 text-orange-500 border-orange-200' },
    not_enrolled: { label: 'N/I', cls: 'bg-slate-100 text-slate-400 border-slate-200' },
  };
  const cfg = CFG[state];
  const label = look.source ? fmt(look.amount) : att?.is_aide ? 'aide' : cfg.label;
  return (
    <span className={cn('w-16 h-7 rounded-full text-xs font-bold border flex items-center justify-center', cfg.cls)}
      title={look.source === 'paid' ? 'Payé à cette séance' : look.source === 'price' ? 'Prix convenu (pas encore payé à cette séance)' : ''}
      data-testid="amount-pill">
      {label}{look.source === 'paid' && <span className="ml-0.5 text-[9px]">✓</span>}
    </span>
  );
}
